import React, { memo, ChangeEvent, useState } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import {
  DATE_RANGE_TYPE,
  DATE_RANGE_TYPE_OPTIONS,
  DAY_OR_HOUR_OPTIONS,
} from 'apps/spot-quote-control-panel/constants/spotBiddingConfig'
import {
  DAY_OR_HOUR,
  IProximalHourCriteria,
  ISpotBiddingRule,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'
import { TextInput } from 'components/atoms'

interface INumDaysOutProps {
  value?: RecursivePartial<IProximalHourCriteria>
  onChange(value: RecursivePartial<ISpotBiddingRule>): void
  error?: string
}

export const HOURS_IN_DAY = 24

export const NumDaysOut = memo<INumDaysOutProps>(
  ({ error, onChange, value: proximalHourCriteria }) => {
    const { rangeEnd = '', rangeStart = '' } = proximalHourCriteria || {}
    const initialRangeType = (() => {
      if (Number(rangeStart) === 0) {
        return DATE_RANGE_TYPE.LESS
      }
      if (Number(rangeEnd) - Number(rangeStart) === HOURS_IN_DAY) {
        return DATE_RANGE_TYPE.EXACTLY
      }
      return DATE_RANGE_TYPE.MORE
    })()

    const [dateType, setDateType] = useState(initialRangeType)

    const initialDayOrHour = (() => {
      if (rangeEnd || rangeStart) {
        return Number(rangeEnd) < 24 && Number(rangeStart) < 24
          ? DAY_OR_HOUR.hours
          : DAY_OR_HOUR.days
      }
      return DAY_OR_HOUR.hours
    })()

    const [dayOrHour, setDayOrHour] = useState(initialDayOrHour)

    const rangeLess = Number(rangeEnd) > 24 ? `${Number(rangeEnd) / HOURS_IN_DAY}` : rangeEnd
    const rangeMore = Number(rangeStart) > 24 ? `${Number(rangeStart) / HOURS_IN_DAY}` : rangeStart
    const initialNumDaysOut = (() => {
      if (dateType === DATE_RANGE_TYPE.LESS) {
        return rangeLess
      }
      if (dateType === DATE_RANGE_TYPE.EXACTLY) {
        return rangeMore
      }
      if (dayOrHour === DAY_OR_HOUR.hours) {
        return rangeMore ? `${Number(rangeStart)}` : ''
      }
      return rangeMore ? `${Math.floor(Number(rangeStart) / HOURS_IN_DAY) - 1}` : ''
    })()

    const [numDaysOut, setNumDaysOut] = useState(initialNumDaysOut)

    const rangeGenerator = (rangeType: DATE_RANGE_TYPE, value: string, dayOrHour: string) => {
      const numHoursOut =
        dayOrHour === DAY_OR_HOUR.days ? `${Number(value) * HOURS_IN_DAY}` : Number(value)
      let ranges = {}
      if (rangeType === DATE_RANGE_TYPE.LESS) {
        ranges = { rangeStart: '', rangeEnd: numHoursOut }
      } else if (rangeType === DATE_RANGE_TYPE.MORE && dayOrHour === DAY_OR_HOUR.days) {
        ranges = { rangeStart: `${Number(numHoursOut) + HOURS_IN_DAY}`, rangeEnd: '' }
      } else if (rangeType === DATE_RANGE_TYPE.EXACTLY && dayOrHour === DAY_OR_HOUR.days) {
        ranges = { rangeStart: numHoursOut, rangeEnd: `${Number(numHoursOut) + HOURS_IN_DAY}` }
      } else if (rangeType === DATE_RANGE_TYPE.MORE && dayOrHour === DAY_OR_HOUR.hours) {
        ranges = { rangeStart: `${Number(numHoursOut)}`, rangeEnd: '' }
      } else if (rangeType === DATE_RANGE_TYPE.EXACTLY && dayOrHour === DAY_OR_HOUR.hours) {
        ranges = { rangeStart: numHoursOut, rangeEnd: `${Number(numHoursOut)}` }
      }
      return ranges
    }

    const dateTypeHandler = (rangeType: string) => {
      setDateType(rangeType as DATE_RANGE_TYPE)
      onChange({
        proximalHourCriteria: {
          ...proximalHourCriteria,
          ...rangeGenerator(rangeType as DATE_RANGE_TYPE, numDaysOut, dayOrHour),
        },
      })
    }

    const numDaysOutHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (Number(value)) {
        setNumDaysOut(value)
        onChange({
          proximalHourCriteria: {
            ...proximalHourCriteria,
            ...rangeGenerator(dateType, value, dayOrHour),
          },
        })
      }
    }

    const dayOrHourTypeHandler = (e: ISelectOption<DAY_OR_HOUR>) => {
      setDayOrHour(e.value)
      onChange({
        proximalHourCriteria: {
          ...proximalHourCriteria,
          ...rangeGenerator(dateType, numDaysOut, e.value),
        },
      })
    }

    return (
      <>
        <div className='col-md-6 col-xs-6'>
          <ButtonGroup
            value={dateType}
            onChange={dateTypeHandler}
            dataTest='modal-date-range-button'>
            {DATE_RANGE_TYPE_OPTIONS.map(option => (
              <Button key={option.label} value={option.value} size='large' fullWidth>
                <div className='rule-header'>{option.label}</div>
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <div className='col-md-3 col-xs-4'>
          <Select
            options={DAY_OR_HOUR_OPTIONS}
            value={dayOrHour}
            onChange={dayOrHourTypeHandler}
            label='Hour/Day'
            placeholder='Select'
            id='modal-hour-day'
            dataTest='modal-hour-day'
            required
          />
        </div>
        <div className='col-md-3 col-xs-4'>
          <TextInput
            label='# Hours/Days Out'
            placeholder='Enter number Hour(s)/Day(s) Out'
            id='modal-num-days-out'
            dataTest='modal-num-days-out'
            value={numDaysOut || ''}
            onChange={numDaysOutHandler}
            validationMessage={error}
            invalid={!!error}
          />
        </div>
      </>
    )
  }
)
